import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';

import { buildTradeRuleProposalCardList, getTradeRules } from './helpers';
import useCustomerOnlyRoute from '../hooks/useCustomerOnlyRoute';
import useViewerMeters from '../hooks/useViewerMeters';

const TradeRuleCustomerProposalsFragment = graphql`
  fragment TradeRuleCustomerProposals_viewer on Viewer {
    id
    viewerUser { id type }
    proposals: rules(first:500, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_PROPOSED) {
      edges {
        node {
          id
          clauses {
            edges {
              node {
                price
                timezone
                id
                monthsOfYear
                daysOfWeek
                  timesOfDay {
                    start  { hours minutes seconds }
                    finish { hours minutes seconds }
                }
                ignoreDaylightSavings
                ignorePublicHolidays
              }
            }
          }
          tradeType
          state
          buyer {
            user { id email givenName familyName }
            tradePoint { id type }
          }
          seller {
            user { id email givenName familyName }
            tradePoint { id type }
          }
          proposedAt
          proposedBy { id email givenName familyName }
        }
      }
    }
  }
`;

/**
 * TradeRuleCustomerProposals functional component displays customer trade rule proposals.
 * @param {object} props - Component props
 * @param {object} props.viewer - Viewer data from GraphQL fragment
 * @param {object} props.router - React Router object providing navigation capabilities
 * @returns {React.ReactElement} The TradeRuleCustomerProposals component
 */
function TradeRuleCustomerProposals({ viewer, router }) {
  const intl = useIntl();
  const environment = useRelayEnvironment();

  const viewerData = useFragment(TradeRuleCustomerProposalsFragment, viewer || null);

  const { proposals, viewerUser } = viewerData || {};
  const { id } = viewerUser || {};
  const { type: userType } = viewerUser || {};

  const { meters, metersLoading } = useViewerMeters(intl, environment, userType);
  useCustomerOnlyRoute({
    intl, router, userType, viewerUser,
  });

  if (!viewer || !router) {
    return <Loading />;
  }

  const tradeRules = getTradeRules(id, proposals);
  const {
    buy = [], counterPartyBuy = [], counterPartySell = [], sell = [],
  } = tradeRules || {};

  const totalBuys = buy.length + counterPartySell.length;
  const totalSells = sell.length + counterPartyBuy.length;

  // i18n strings
  const pageTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.page.title', defaultMessage: 'Enosi - Manage my proposals' });
  const tradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.breadcrumbs.trade_rules.label', defaultMessage: 'Trade Rules' });
  const proposalsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.breadcrumbs.proposals.label', defaultMessage: 'Proposals' });
  const headingTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.heading.alias.trade_rules.label', defaultMessage: 'Trade Rules' });
  const headingProposalsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.heading.context.proposals.label', defaultMessage: 'Proposals' });
  const proposedTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.proposed_trades.title', defaultMessage: 'Proposed Trades' });
  const sellingLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.selling.label', defaultMessage: 'Selling' });
  const buyingLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.buying.label', defaultMessage: 'Buying' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: tradeRulesLabel, path: '/trade-rules' },
          { name: proposalsLabel },
        ]}
      />
      <Heading
        title={headingTradeRulesLabel}
        context={headingProposalsLabel}
      />

      <Card className="mt-4 mb-4 trade-proposals">
        <CardBody>
          <h2>{proposedTradesTitle}</h2>
          <Row>
            <Col xs="12" sm="12" md="6">
              <h3>{buyingLabel}</h3>
              <p data-testid="buying">
                {intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.buying.text',
                    defaultMessage: 'You have {totalBuys} proposed trade rules to buy energy.',
                  },
                  {
                    totalBuys: <strong>{totalBuys}</strong>,
                  },
                )}
              </p>
              {buildTradeRuleProposalCardList(
                intl,
                buy,
                meters,
                TRADE_DIRECTION_BUY,
                metersLoading,
              )}
              {buildTradeRuleProposalCardList(
                intl,
                counterPartySell,
                meters,
                TRADE_DIRECTION_SELL,
                metersLoading,
              )}
            </Col>

            <Col xs="12" sm="12" md="6">
              <h3>{sellingLabel}</h3>
              <p data-testid="selling">
                {intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.selling.text',
                    defaultMessage: 'You have {totalSells} proposed trade rules to sell energy.',
                  },
                  {
                    totalSells: <strong>{totalSells}</strong>,
                  },
                )}
              </p>
              {buildTradeRuleProposalCardList(
                intl,
                sell,
                meters,
                TRADE_DIRECTION_SELL,
                metersLoading,
              )}
              {buildTradeRuleProposalCardList(
                intl,
                counterPartyBuy,
                meters,
                TRADE_DIRECTION_BUY,
                metersLoading,
              )}
            </Col>

          </Row>
        </CardBody>
      </Card>
    </>
  );
}

TradeRuleCustomerProposals.propTypes = {
  viewer: PropTypes.shape({
    viewerUser: PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
    }),
    proposals: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string,
          }),
        }),
      ),
    }),
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
};

export default TradeRuleCustomerProposals;
